import { ApplicationInsights, IEventTelemetry } from '@microsoft/applicationinsights-web';

const TELEMETRY_ENABLED = (import.meta.env.VITE_TELEMETRY_ENABLED || 'false').toLowerCase() === 'true';
const APPINSIGHTS_CONNECTION_STRING = import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING || '';
const DEPLOYMENT_VERSION = import.meta.env.VITE_APP_VERSION || 'unknown';
const ENVIRONMENT = import.meta.env.MODE || 'development';

let appInsights: ApplicationInsights | null = null;
let initialized = false;

const getStatusFamily = (statusCode?: number): string => {
  if (!statusCode || Number.isNaN(statusCode)) {
    return 'unknown';
  }
  return `${Math.floor(statusCode / 100)}xx`;
};

const sanitizePath = (path: string): string => {
  try {
    const url = new URL(path, window.location.origin);
    return url.pathname;
  } catch {
    return path.split('?')[0] || '/';
  }
};

const getEndpointGroup = (path: string): string => {
  const normalized = sanitizePath(path);
  const parts = normalized.split('/').filter(Boolean);
  if (parts.length === 0) {
    return 'root';
  }
  return parts[0];
};

const safeTrackEvent = (event: IEventTelemetry, properties?: Record<string, string>, measurements?: Record<string, number>) => {
  if (!appInsights) {
    return;
  }
  appInsights.trackEvent({
    ...event,
    properties,
    measurements,
  });
};

const setupGlobalErrorHandlers = () => {
  window.addEventListener('error', (event: ErrorEvent) => {
    safeTrackEvent(
      { name: 'ui.error.unhandled' },
      {
        feature: 'frontend',
        error_type: 'window.error',
        message: event.message || 'unknown',
        filename: sanitizePath(event.filename || ''),
        deployment_version: DEPLOYMENT_VERSION,
      },
      {
        line: event.lineno || 0,
        column: event.colno || 0,
      },
    );
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const reason = typeof event.reason === 'string' ? event.reason : event.reason?.message || 'unknown';
    safeTrackEvent(
      { name: 'ui.error.unhandled_promise' },
      {
        feature: 'frontend',
        error_type: 'unhandledrejection',
        message: reason,
        deployment_version: DEPLOYMENT_VERSION,
      },
    );
  });
};

export const initializeClientTelemetry = (): boolean => {
  if (initialized) {
    return Boolean(appInsights);
  }
  initialized = true;

  if (!TELEMETRY_ENABLED || !APPINSIGHTS_CONNECTION_STRING) {
    return false;
  }

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString: APPINSIGHTS_CONNECTION_STRING,
        disableFetchTracking: true,
        disableAjaxTracking: true,
        enableAutoRouteTracking: false,
        samplingPercentage: 20,
      },
    });
    appInsights.loadAppInsights();
    setupGlobalErrorHandlers();

    safeTrackEvent(
      { name: 'ui.bootstrap.initialized' },
      {
        feature: 'frontend',
        environment: ENVIRONMENT,
        deployment_version: DEPLOYMENT_VERSION,
      },
    );

    return true;
  } catch {
    appInsights = null;
    return false;
  }
};

export const createCorrelationId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const trackNavigationEvent = (path: string) => {
  safeTrackEvent(
    { name: 'ui.navigation.route_change' },
    {
      feature: 'navigation',
      path: sanitizePath(path),
      endpoint_group: getEndpointGroup(path),
      deployment_version: DEPLOYMENT_VERSION,
    },
  );
};

export const trackVersionEvent = (eventType: 'initial' | 'unchanged' | 'changed', previousVersion: string | null, currentVersion: string) => {
  safeTrackEvent(
    { name: 'ui.version.check' },
    {
      feature: 'version',
      event_type: eventType,
      previous_version: previousVersion || 'none',
      current_version: currentVersion,
      deployment_version: DEPLOYMENT_VERSION,
    },
  );
};

export const trackApiOutcome = (params: {
  endpoint: string;
  method: string;
  correlationId: string;
  latencyMs: number;
  statusCode?: number;
  networkFailure: boolean;
  maintenanceMode: boolean;
}) => {
  const statusFamily = getStatusFamily(params.statusCode);
  safeTrackEvent(
    { name: 'ui.api.request' },
    {
      feature: 'api',
      endpoint: sanitizePath(params.endpoint),
      endpoint_group: getEndpointGroup(params.endpoint),
      method: params.method.toUpperCase(),
      status_family: statusFamily,
      correlation_id: params.correlationId,
      network_failure: String(params.networkFailure),
      maintenance_mode: String(params.maintenanceMode),
      deployment_version: DEPLOYMENT_VERSION,
    },
    {
      latency_ms: Math.round(params.latencyMs * 100) / 100,
      status_code: params.statusCode || 0,
    },
  );
};
